// src/features/analytics/components/MediaConcurrencyCard.tsx
//
// Per-stream concurrency series (dashboard ADR-009): minute buckets for
// game-length windows (≤ 33 h, the gateway's chart-point cap), hourly
// beyond; the presence retention (90 days) clamps the window like the
// account concurrency trend. Reuses ConcurrencyTrendCard's presentation.

import { CONCURRENCY_MAX_CHART_POINTS } from '@/lib/constants/analytics';
import { HEATMAP_MAX_RANGE_DAYS, type DayZone } from '@/shared/utils/utcRange';
import { useAnalyticsMediaConcurrency } from '../useAnalyticsMediaStream';
import type { EventWindow } from '../eventWindow';
import { ConcurrencyTrendCard } from './ConcurrencyTrendCard';

interface MediaConcurrencyCardProps {
  cid: number | null;
  mediaId: string;
  window: EventWindow;
  /** Plain line for a window with no viewers. */
  emptyCopy: string;
  zone: DayZone;
}

/** Minute points up to the gateway's 2,000-point cap (≈33 h); hour points beyond. */
const MINUTE_CAP_SECONDS = CONCURRENCY_MAX_CHART_POINTS * 60;

export function MediaConcurrencyCard({ cid, mediaId, window, emptyCopy, zone }: MediaConcurrencyCardProps) {
  const earliest = window.to - HEATMAP_MAX_RANGE_DAYS * 86400;
  const range = { from: Math.max(window.from, earliest), to: window.to };
  const interval = range.to - range.from <= MINUTE_CAP_SECONDS ? 'minute' : 'hour';
  const query = useAnalyticsMediaConcurrency(true, cid, mediaId, range, interval);
  return (
    <ConcurrencyTrendCard
      concurrency={query.data}
      range={range}
      isLoading={query.isLoading}
      isError={query.isError}
      error={query.error}
      onRetry={query.refetch}
      emptyCopy={emptyCopy}
      window={range}
      zone={zone}
    />
  );
}
